import './inter-800.css';
import './inter-900.css';
import './poster-fonts.css';
import { renderToStaticMarkup } from 'react-dom/server';
import { useId } from 'react';
import type { PosterSubmissionFields } from '@rudi/shared/types/clinical-trials';
import {
  CLASSIC_LAYERS,
  posterBlockFont,
  loadPosterFonts,
  posterT,
  DEFAULT_POSTER_IMAGE,
  POSTER_H,
  POSTER_THEMES,
  POSTER_W,
  PORTRAIT_FRAME,
  fitPosterText,
  posterTextBlocks,
  type PosterStyle,
} from './poster-design';
import { JOURNEY_QR_IMAGE, PosterJourneyArtwork } from './PosterJourneyArtwork';

export function PosterArtwork({
  fields,
  style,
  hiddenTextKey,
  omitText = false,
  images = {},
}: {
  fields: PosterSubmissionFields;
  style: PosterStyle;
  hiddenTextKey?: string | null;
  omitText?: boolean;
  images?: Record<string, string>;
}) {
  const portraitClipId = `portrait-${useId().replace(/:/g, '')}`;
  const theme = POSTER_THEMES[style];
  const classic = style === 'classic';
  const journey = style === 'journey';
  const image = fields.bottomImageDataUrl ?? DEFAULT_POSTER_IMAGE;
  const blocks = posterTextBlocks(fields, style);
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${POSTER_W} ${POSTER_H}`}
      width={POSTER_W}
      height={POSTER_H}
      className="h-full w-full"
      aria-hidden="true"
    >
      <rect width={POSTER_W} height={POSTER_H} fill={theme.background} />
      {classic ? (
        <>
          {CLASSIC_LAYERS.map(url => (
            <image key={url} href={images[url] ?? url} width={POSTER_W} height={POSTER_H} />
          ))}
          <path d="M0 660H1165a75 75 0 0 1 75 75v0a75 75 0 0 1-75 75H0Z" fill="#050505" />
          <rect
            x={fields.portraitHidden ? 24 : 170}
            y="2015"
            width={fields.portraitHidden ? 1543 : 1397}
            height="200"
            rx="95"
            fill="white"
          />
        </>
      ) : journey ? (
        <PosterJourneyArtwork qrImage={images[JOURNEY_QR_IMAGE] ?? JOURNEY_QR_IMAGE} />
      ) : (
        <>
          {style === 'official' && (
            <>
              <rect width={POSTER_W} height="42" fill={theme.accent} />
              <rect x="130" y="325" width="1327" height="3" fill={theme.accent} />
              <rect x="100" y="1235" width="1387" height="450" rx="30" fill={theme.soft} />
              <rect x="130" y="1780" width="1327" height="3" fill={theme.accent} />
            </>
          )}
          {style === 'doctor' && (
            <>
              <circle cx="1587" cy="0" r="420" fill={theme.soft} />
              <rect x="75" y="110" width="8" height="560" fill={theme.accent} />
              <rect x="100" y="710" width="1387" height="165" rx="30" fill={theme.soft} />
              <path d="M0 1850Q790 1650 1587 1810V2245H0Z" fill={theme.soft} />
            </>
          )}
          {style === 'patient' && (
            <>
              <circle cx="790" cy="330" r="590" fill={theme.soft} opacity="0.55" />
              <rect x="95" y="710" width="1397" height="1010" rx="48" fill="#ffffff" />
              <circle cx="1480" cy="2160" r="260" fill={theme.soft} />
            </>
          )}
          {style === 'community' && (
            <>
              <circle cx="1540" cy="0" r="410" fill={theme.soft} />
              <circle cx="40" cy="2245" r="450" fill={theme.soft} />
              <rect x="130" y="115" width="160" height="10" fill={theme.accent} />
              <rect x="100" y="710" width="1387" height="175" rx="30" fill={theme.soft} />
              <path d="M130 1770H1457" stroke={theme.accent} strokeWidth="3" />
            </>
          )}
          <text
            x="1457"
            y="2160"
            textAnchor="end"
            fill={theme.accent}
            fontFamily="Arial, sans-serif"
            fontWeight="700"
            fontSize="34"
            letterSpacing="8"
          >
            {posterT('clinicalTrials.posterEditor.brand')}
          </text>
        </>
      )}
      {blocks.map(block => {
        const { lines, size } = fitPosterText(block);
        return (
          <g key={block.key} visibility={block.key === hiddenTextKey ? 'hidden' : undefined}>
            {block.field === 'bullets' && !journey && (
              <circle
                cx={block.x - 40}
                cy={block.y + size * 0.55}
                r={classic ? 9 : 7}
                fill={classic ? theme.ink : theme.accent}
              />
            )}
            {block.field === 'checklist' && !journey && (
              <g transform={`translate(${block.x - 82} ${block.y + 1})`}>
                {classic && (
                  <circle
                    cx="24"
                    cy="29"
                    r="23"
                    fill="none"
                    stroke={theme.accent}
                    strokeWidth="4"
                  />
                )}
                <path
                  d="M10 28l13 13 27-34"
                  stroke={theme.accent}
                  strokeWidth="5"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
            )}
            {!omitText && (
              <text
                data-poster-text={block.key}
                fill={block.color}
                fontFamily={posterBlockFont(block)}
                fontSize={size}
                fontWeight={block.weight}
                letterSpacing={block.letterSpacing ?? 0}
                textAnchor={block.align === 'center' ? 'middle' : 'start'}
              >
                {lines.map((line, index) => (
                  <tspan
                    key={index}
                    x={block.x + (block.align === 'center' ? block.width / 2 : 0)}
                    y={block.y + size * 0.95 + index * size * (block.lineHeight ?? 1.25)}
                  >
                    {line}
                  </tspan>
                ))}
              </text>
            )}
          </g>
        );
      })}
      <defs>
        <clipPath id={portraitClipId}>
          <ellipse
            cx={PORTRAIT_FRAME.x + PORTRAIT_FRAME.width / 2}
            cy={PORTRAIT_FRAME.y + PORTRAIT_FRAME.height / 2}
            rx={PORTRAIT_FRAME.width / 2}
            ry={PORTRAIT_FRAME.height / 2}
          />
        </clipPath>
      </defs>
      {!fields.portraitHidden && (
        <image
          data-poster-portrait
          href={images[image] ?? image}
          {...PORTRAIT_FRAME}
          clipPath={`url(#${portraitClipId})`}
          preserveAspectRatio="xMidYMid slice"
        />
      )}
    </svg>
  );
}

export async function renderPosterCanvas(
  fields: PosterSubmissionFields,
  style: PosterStyle
): Promise<HTMLCanvasElement> {
  await loadPosterFonts();
  await document.fonts.ready;
  const urls = [
    ...(style === 'classic' ? CLASSIC_LAYERS : []),
    ...(style === 'journey' ? [JOURNEY_QR_IMAGE] : []),
    ...(fields.portraitHidden ? [] : [fields.bottomImageDataUrl ?? DEFAULT_POSTER_IMAGE]),
  ];
  const images = Object.fromEntries(
    await Promise.all(
      urls.map(async url => {
        if (url.startsWith('data:')) return [url, url];
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Could not load poster image: ${response.status}`);
        return [url, await blobToDataUrl(await response.blob())];
      })
    )
  );
  const markup = renderToStaticMarkup(
    <PosterArtwork fields={fields} style={style} images={images} omitText />
  );
  const svgUrl = await blobToDataUrl(new Blob([markup], { type: 'image/svg+xml;charset=utf-8' }));
  const image = new Image();
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('Could not render the poster.'));
    image.src = svgUrl;
  });
  const canvas = document.createElement('canvas');
  canvas.width = 2480;
  canvas.height = 3508;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas rendering is not available.');
  context.scale(canvas.width / POSTER_W, canvas.height / POSTER_H);
  context.drawImage(image, 0, 0, POSTER_W, POSTER_H);
  // Canvas uses the loaded font files; standalone SVG images cannot use page fonts.
  for (const block of posterTextBlocks(fields, style)) {
    const { lines, size } = fitPosterText(block);
    context.font = `${block.weight} ${size}px ${posterBlockFont(block)}`;
    context.fillStyle = block.color;
    context.textAlign = block.align;
    context.textBaseline = 'alphabetic';
    context.letterSpacing = `${block.letterSpacing ?? 0}px`;
    lines.forEach((line, index) =>
      context.fillText(
        line,
        block.x + (block.align === 'center' ? block.width / 2 : 0),
        block.y + size * 0.95 + index * size * (block.lineHeight ?? 1.25)
      )
    );
  }
  return canvas;
}

export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}
